import type { ReferralInfo } from '@getstep/sdk/dist/http/models/Referrals'
import { notNullish } from '@getstep/sdk/dist/util/Assert'
import { asPossessive } from '@getstep/sdk/dist/util/Names'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import type { DeepPartial } from 'react-hook-form'

import type { AppLinker } from './AppLinker'
import { nullLinker } from './NullLinker'
import type { AppLinkParameters, SMSTemplate } from './parameters'
import { DEFAULT_PARAMETERS, mergeParameters } from './parameters'
import type { DeviceStore } from '@lib/store/DeviceStore'
import { tokenStore } from '@lib/store/JwtTokenStore'
import { PersistentStore } from '@lib/store/persistence/PersistentStore'
import { formatAsAmount } from '@lib/utils/number'
import { TEXT } from '@lib/utils/text'
import { extendConsole } from '@shared/logging/console'
import { LINK } from '@shared/util/link'

const logger = extendConsole('LinkingStore')

/**
 * Generates personalized app links, stores them, and sends SMS.
 * The parameters are stored in LocalStorage.
 */
export class LinkingStore extends PersistentStore {
    static readonly className = 'LinkingStore'

    linker: AppLinker = nullLinker

    loaded = false

    personalizedLink?: string

    parameters: AppLinkParameters = DEFAULT_PARAMETERS

    constructor(private deviceStore: DeviceStore) {
        super()
        makeObservable(this, {
            defaultLink: computed,
            appDownloadLink: computed,
            data: computed,
            loaded: observable,
            hasLinker: computed,
            linker: observable,
            personalizedLink: observable,
            parameters: observable,
        })
    }

    /**
     * Returns the link for the current user.
     * If the linker is unavailable, returns the default non-personalized link.
     */
    get appDownloadLink() {
        if (!this.defaultLink) return

        // IMPORTANT: This check ensures MobX properly tracks the hasLinker observable.
        // Without this explicit check, React components may not re-render when the linker
        // loads asynchronously (AppsFlyerLinker via GTM or BranchLinker initialization).
        // The issue occurs because personalizedLink can be set before React completes
        // reconciliation, causing the link to update outside the render cycle.
        // DO NOT REMOVE - this fixes timing issues with async linker initialization.
        if (!this.hasLinker) return this.defaultLink

        return this.personalizedLink ?? this.defaultLink
    }

    /**
     * Link data with defaults applied.
     */
    get data() {
        return this.parameters.data
    }

    /**
     * Returns true if linking provider has been assigned.
     */
    get hasLinker() {
        return this.linker !== nullLinker
    }

    /**
     * Picks the right non-personalized link for the platform.
     */
    get defaultLink(): string | void {
        const { os, userAgent } = this.deviceStore
        if (!os) return
        if (os === 'Android') return LINK.app.GooglePlay()
        if (os === 'iOS') return LINK.app.AppStore()
        logger.error(`No download link for userAgent`, userAgent)
        // We're on desktop, unsupported mobile OS, or something's wrong with the sniffer.
        return
    }

    /**
     * Values for PersonalizationProvider.
     */
    getPersonalizations(mounted = false) {
        const data = mounted ? this.data : DEFAULT_PARAMETERS.data

        return {
            ...data,
            amountFormatted: formatAsAmount(
                data['amount_value'],
                data['amount_currency'],
            ),
            expiresAt: TEXT.after7Days,
            sponseeName: data['sponsee_full_name'],
            pictureUrl: data['picture_url'],
            sponseeNamePossessive: notNullish(data['sponsee_full_name'])
                ? asPossessive(data['sponsee_full_name'])
                : undefined,
            firstName: data['sender_first_name'],
            fullName: data['sender_full_name'],
            name: data['sender_full_name'],
            phoneSuffix: data['sender_phone_suffix'],
            maskedCardNumber: '',
            referredAwardFormatted: data['referred_award_formatted'],
        }
    }

    /**
     * Swaps the linking provider for a different one.
     */
    setLinker = async (linker: AppLinker) => {
        await runInAction(async () => {
            if (linker === this.linker) return
            this.loaded = false
            this.linker = linker
            await this.load()
            logger.debug('changed linker', linker)
        })
    }

    /**
     * Texts the parametrized link to the phoneNumber
     */
    sendDownloadLinkInSMS = async (
        phoneNumber: string,
        { customMessage, templateId, deepLinkValue }: SMSOptions = {},
    ) => {
        // Get current userId from tokenStore for attribution
        const userId = tokenStore.refreshToken?.subject

        const params: AppLinkParameters = mergeParameters(this.parameters, {
            data: {
                $custom_sms_text: customMessage,
                segment_user_id: userId,
            },
            templateId,
            deepLinkValue,
        })

        await this.linker.sendSMS(phoneNumber, params, !!customMessage)
    }

    /**
     * Updates the link with the desktop url.
     */
    updateCanonicalUrl = async (url: string) => {
        await this.update(
            {
                data: {
                    $fallback_url: url,
                    $desktop_url: url,
                    $canonical_url: url,
                },
            },
            'canonical',
        )
    }

    /**
     * Updates the link with the userId.
     */
    updateUserId = async (userId?: string) => {
        await this.update({ data: { segment_user_id: userId } }, 'user')
    }

    /**
     * Updates the link with the referral code.
     */
    updateReferralInfo = async (
        code: string,
        info?: ReferralInfo,
        options: { expedited_onboarding?: boolean } = {},
    ) => {
        const { referredAward: award, firstName, fullName } = info ?? {}

        await this.update(
            {
                data: {
                    ...info,
                    ...options,
                    code,
                    sender_first_name: firstName,
                    sender_full_name: fullName,
                    referred_award_formatted:
                        award && formatAsAmount(award.value, award.currency),
                },
            },
            'referral',
        )
    }

    /**
     * Updates the link with the given parameters.
     */
    update = async (
        input: DeepPartial<AppLinkParameters>,
        cause?:
            | 'initial'
            | 'referral'
            | 'user'
            | 'feature'
            | 'sms'
            | 'canonical'
            | 'smart_script_ready',
    ) => {
        const oldParameters = this.parameters
        const newParameters = mergeParameters(oldParameters, input)

        if (this.deviceStore.mobile) {
            newParameters.data['$fallback_url'] = undefined
        }

        logger.debug(
            cause,
            'update',
            cloneDeep({ oldParameters, newParameters }),
        )

        if (isEqual(newParameters, oldParameters) && cause !== 'initial') return

        this.parameters = newParameters
        this.personalizedLink =
            (await this.linker.link(newParameters)) || undefined

        logger.debug('update link', this.personalizedLink, { newParameters })

        // Fire AppsFlyer impression for view-through attribution
        // Only on mobile devices after the OneLink URL has been generated
        // This will attribute installs to ad impressions even if the user does not install during the earlypay flow,
        // but still installs the app at a later stage
        // https://support.appsflyer.com/hc/en-us/articles/210084473-Measure-view-through-engagements
        if (
            this.deviceStore.mobile &&
            this.personalizedLink &&
            typeof window !== 'undefined' &&
            window.AF_SMART_SCRIPT?.fireImpressionsLink
        ) {
            window.AF_SMART_SCRIPT.fireImpressionsLink()
            logger.debug('Fired AppsFlyer impression link')
        }

        this.persist()
    }

    load = async () => {
        if (this.loaded || !this.hasLinker) return

        await runInAction(async () => {
            const { linker } = this

            const parameters = await linker.init()
            if (parameters) await this.update(parameters, 'initial')
            this.loaded = true

            // If Smart Script isn't loaded yet, wait for it and regenerate link
            // This ensures we use the Smart Script's enhanced link generation
            // instead of falling back to the organic URL
            if (
                typeof window !== 'undefined' &&
                !window.AF_SMART_SCRIPT?.generateOneLinkURL
            ) {
                this.waitForSmartScriptAndRegenerate()
            }
        })
    }

    /**
     * Waits for AppsFlyer Smart Script to load, then regenerates the link
     *
     * This ensures that if the Smart Script wasn't ready during initial page load,
     * we regenerate the link once it becomes available. The Smart Script provides
     * enhanced link generation with better app opening behavior.
     */
    private waitForSmartScriptAndRegenerate = async () => {
        try {
            // Wait for Smart Script to load (max 5 seconds)
            await this.waitForSmartScript(5000)
            logger.debug(
                'Smart Script loaded, regenerating link with enhanced parameters',
            )
            // Regenerate link with Smart Script now available
            await this.update(this.parameters, 'smart_script_ready')
        } catch (error) {
            logger.debug(
                'Smart Script not loaded after timeout, keeping fallback URL',
            )
        }
    }

    /**
     * Waits for AppsFlyer Smart Script to become available
     * @param timeout - Maximum time to wait in milliseconds
     * @returns Promise that resolves when Smart Script is ready or rejects on timeout
     */
    private waitForSmartScript = (timeout: number): Promise<void> => {
        return new Promise((resolve, reject) => {
            const startTime = Date.now()
            const checkInterval = 100 // Check every 100ms

            const interval = setInterval(() => {
                const loaded =
                    typeof window.AF_SMART_SCRIPT?.generateOneLinkURL ===
                    'function'
                if (!loaded && Date.now() - startTime < timeout) return
                // Handle result
                clearInterval(interval)
                if (loaded) {
                    resolve()
                } else {
                    reject(new Error('Smart Script load timeout'))
                }
            }, checkInterval)
        })
    }
}

interface SMSOptions {
    customMessage?: string
    templateId?: SMSTemplate
    deepLinkValue?: string
}
